import { useCallback, useRef } from "react";
import { styled, TextField } from "@mui/material";
import { MapLocation } from "types/map";
import useAutocomplete, {
  Autocomplete,
} from "hooks/maps/useAutocomplete";

const SearchField = styled(TextField)(() => ({
  width: "100%",
  flexGrow: 0,
  flexShrink: 0,
  alignSelf: "stretch",
}));

type Props = {
  disabled?: boolean;
  onAdd: (location: MapLocation) => void;
};

const TripLocationSearch = ({ disabled = false, onAdd }: Props) => {
  const searchFieldRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<Autocomplete | null>(null);

  const handleInitialized = useCallback((autocomplete: Autocomplete) => {
    autocompleteRef.current = autocomplete;
  }, []);

  const handlePlaceChange = useCallback(
    (autocomplete: Autocomplete) => {
      const place = autocomplete.getPlace();
      const { formatted_address, geometry, name, types } = place;

      console.log("[Map] Location place selected", formatted_address);

      const lat = geometry?.location?.lat?.() ?? null;
      const lng = geometry?.location?.lng?.() ?? null;

      if (lat == null || lng == null) {
        console.warn(
          "[TripLocationSearch] Place selected without geometry — not added"
        );
        return;
      }

      onAdd({
        formattedAddress: formatted_address ?? name ?? "",
        latitude: lat,
        longitude: lng,
        placeName: name || undefined,
        placeTypes: types?.length ? [...types] : undefined,
      });

      if (searchFieldRef.current) {
        searchFieldRef.current.value = "";
      }
    },
    [onAdd]
  );

  useAutocomplete({
    referenceBinding: searchFieldRef,
    onInitialized: handleInitialized,
    onPlaceChange: handlePlaceChange,
  });

  return (
    <SearchField
      fullWidth
      disabled={disabled}
      inputRef={searchFieldRef}
      variant="outlined"
      size="small"
      label="Add Location to Trip Planner"
      placeholder="Add a stop to your trip"
      defaultValue=""
      onFocus={(event: React.FocusEvent<HTMLInputElement>) =>
        event.target.select()
      }
    />
  );
};

export default TripLocationSearch;
